import { useTranslation } from 'react-i18next'

import { CountryDropdown } from '@/components/ui/CountryDropdown'
import { Field, Select, Textarea } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { Input } from '@/components/ui/Input'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { contactToPayload, type ContactPayload } from '@/features/directories/api/useDirectory'
import { ContactsEditor } from '@/features/directories/components/ContactsEditor'
import { useContactsEditable } from '@/features/directories/useContactsEditable'
import { findCountry } from '@/lib/countries'
import type { Counterparty, CounterpartyCategory } from '@/types/domain'

/* The counterparty editor (S-22b) — create and edit, from the section, the
 * record page and the three pickers. It replaced the directory's participant
 * sheet: one record with a CATEGORY instead of seven types.
 *
 * ── Why an edit takes `Counterparty`, never a list row ──
 *
 * The contacts go back whole: a contact missing from the list is DELETED. The
 * list read carries no contacts at all, and the old directory seeded its edit
 * form from a list row whose contacts were `[]` — so every save from the list
 * wiped the record's contacts. `record` is typed as the single read
 * (`useCounterparty(id)`), and `CounterpartyRow` has no `contacts` member, so
 * that mistake no longer type-checks.
 *
 * ── The category ──
 *
 * Required, and chosen rather than defaulted whenever there is more than one
 * to choose from: a category decides which pickers offer the record (a leg
 * offers carriers, forwarders and drivers), and a silent default is how a
 * customs broker ends up offered as a haulier. Editable on an edit (owner,
 * 2026-10-17). A stored category outside the host's list stays offered and
 * selected — a picker never drops a value it does not recognise.
 */

export interface CounterpartyDraft {
  name: string
  /** '' until one is chosen. */
  category: CounterpartyCategory | ''
  taxId: string
  registrationId: string
  country: string
  address: string
  contacts: ContactPayload[]
}

export interface CounterpartyFormSheetProps {
  open: boolean
  onClose: () => void
  /** The record to edit, from the SINGLE read — absent for a new one. */
  record?: Counterparty
  /** The draft, with its category chosen; the host trims and sends it. */
  onSubmit: (values: CounterpartyDraft & { category: CounterpartyCategory }) => void
  pending?: boolean
  /** The server's refusal of the last save, already worded — see
   *  `counterpartySaveErrorMessage`. Shown above the fields: the page behind
   *  the sheet is under its scrim, so an error rendered there is a Save that
   *  seems to do nothing. */
  error?: string
  /** The categories this host offers — every one on the section and the
   *  payment picker, the three that move cargo on a leg. */
  categories: readonly CounterpartyCategory[]
  /** The sheet's title from a host that knows WHAT is being added — the carrier
   *  picker says «Новый перевозчик» (QW-2, D-4). Omitted, the section's
   *  new/edit title. */
  title?: string
}

export function CounterpartyFormSheet({
  open,
  onClose,
  record,
  onSubmit,
  pending,
  error,
  categories,
  title,
}: CounterpartyFormSheetProps) {
  const { t } = useTranslation()
  const contactsEditable = useContactsEditable(record)
  const [draft, setDraft, touched, markTouched, dirty] = useFormDraft<CounterpartyDraft>(
    open ? (record?.id ?? 'new') : null,
    () =>
      record
        ? {
            name: record.name,
            category: record.category,
            taxId: record.taxId ?? '',
            registrationId: record.registrationId ?? '',
            country: record.country,
            address: record.address ?? '',
            contacts: record.contacts.map(contactToPayload),
          }
        : { ...emptyCounterparty, category: categories.length === 1 ? categories[0]! : '' },
  )

  /* The host's list, plus the stored category when the host does not offer
     it: an edit of a customs broker from the carrier picker must not open
     with an empty select and save a different category on the way out. */
  const offered: CounterpartyCategory[] =
    record && !categories.includes(record.category)
      ? [record.category, ...categories]
      : [...categories]

  const nameError = touched && !draft.name.trim() ? t('common.form.required') : undefined
  const categoryError = touched && draft.category === '' ? t('common.form.required') : undefined
  const invalid = !draft.name.trim() || draft.category === ''

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={title ?? (record ? t('counterparties.edit') : t('counterparties.new'))}
      pending={pending}
      dirty={dirty}
      error={touched && invalid ? t('common.form.fixFields') : error}
      onSubmit={() => {
        markTouched()
        if (draft.category === '' || !draft.name.trim()) return
        onSubmit({ ...draft, category: draft.category })
      }}
    >
      <Field label={t('common.fields.name')} required error={nameError}>
        {(props) => (
          <Input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <Field label={t('counterparties.form.category')} required error={categoryError}>
        {(props) => (
          <Select<CounterpartyCategory | ''>
            value={draft.category}
            onChange={(category) => setDraft({ ...draft, category })}
            placeholder={t('common.form.selectPlaceholder')}
            options={offered.map((category) => ({
              value: category,
              label: t(`counterpartyCategory.${category}`),
            }))}
            {...props}
          />
        )}
      </Field>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('counterparties.form.taxId')}>
          {(props) => (
            <Input
              value={draft.taxId}
              onChange={(e) => setDraft({ ...draft, taxId: e.target.value })}
              {...props}
            />
          )}
        </Field>

        <Field label={t('counterparties.form.registrationId')}>
          {(props) => (
            <Input
              value={draft.registrationId}
              onChange={(e) => setDraft({ ...draft, registrationId: e.target.value })}
              {...props}
            />
          )}
        </Field>
      </div>

      {/* The picker, not a text field: typed, one country arrives as
          "Kazakhstan", "Казахстан", "KZ" and "Респ. Казахстан" — four
          spellings no report can group. A country saved as free text stays
          selected as carried over, so nothing stored is rejected. */}
      <Field label={t('common.fields.country')}>
        {(props) => (
          <CountryDropdown
            value={draft.country}
            onChange={(country) => setDraft({ ...draft, country })}
            {...props}
          />
        )}
      </Field>

      <Field label={t('counterparties.form.address')}>
        {(props) => (
          <Textarea
            value={draft.address}
            onChange={(e) => setDraft({ ...draft, address: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <ContactsEditor
        contacts={draft.contacts}
        editable={contactsEditable}
        defaultCountry={findCountry(draft.country)?.code}
        onChange={(contacts) => setDraft({ ...draft, contacts })}
      />
    </FormSheet>
  )
}

const emptyCounterparty: CounterpartyDraft = {
  name: '',
  category: '',
  taxId: '',
  registrationId: '',
  country: '',
  address: '',
  contacts: [],
}
